import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Scale, Percent, TrendingDown, AlertTriangle, Trophy, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { Card } from "../lib/ui";
import { fmtRp, fmtNum } from "../lib/constants";
import { useT } from "../lib/i18n";

const pct = (v) => (v == null ? "—" : `${v}%`);

export default function MarginInsights() {
  const { t } = useT();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/pricing/margin-dashboard").then(({ data }) => setD(data)).catch(() => setD({})); }, []);

  if (!d) return <div className="mt-8 h-40 animate-pulse rounded-3xl bg-stone-100" data-testid="margin-insights-loading" />;
  const s = d.summary || {};
  if (!s.item_count) return null; // no finished goods yet → hide

  const ranking = (d.margin_ranking || []).slice(0, 3);
  const losers = (d.margin_losers || []).slice(0, 3);

  return (
    <div className="mt-8" data-testid="margin-insights">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Scale size={18} className="text-primary" />
          <h2 className="font-heading text-sm font-extrabold uppercase tracking-wide text-ink-soft">{t("Margin Dashboard")}</h2>
        </div>
        <button onClick={() => navigate("/margin-dashboard")} className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid="margin-insights-link">
          {t("Margin Dashboard")} <ArrowRight size={14} />
        </button>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Kpi icon={Percent} label={t("Avg Central Margin")} value={pct(s.avg_central_margin_pct)} tone="primary" testid="margin-kpi-central" />
        <Kpi icon={Percent} label={t("Avg Branch Margin")} value={pct(s.avg_branch_margin_pct)} tone="info" testid="margin-kpi-branch" />
        <Kpi icon={AlertTriangle} label={t("Items Below Threshold")} value={fmtNum(s.below_threshold ?? 0)} tone="warn" testid="margin-kpi-below" />
        <Kpi icon={TrendingDown} label={t("Selling Below HPP")} value={fmtNum(s.selling_below_hpp ?? 0)} tone="danger" testid="margin-kpi-loss" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <RankCard title={t("Margin Ranking")} subtitle={t("Highest-margin products")} icon={Trophy} rows={ranking} positive testid="margin-ranking" t={t} />
        <RankCard title={t("Margin Losers")} subtitle={t("Lowest-margin products")} icon={TrendingDown} rows={losers} testid="margin-losers" t={t} />
      </div>
    </div>
  );
}

function RankCard({ title, subtitle, icon: Icon, rows, positive, testid, t }) {
  return (
    <Card className="p-4 sm:p-5" data-testid={testid}>
      <div className="mb-3 flex items-center gap-2"><span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary-light text-primary"><Icon size={16} /></span>
        <div><h3 className="font-heading text-sm font-extrabold text-ink">{title}</h3><p className="text-[11px] text-ink-mute">{subtitle}</p></div></div>
      {rows.length ? (
        <div className="space-y-1.5">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2 text-sm" data-testid={`${testid}-${i}`}>
              <span className="flex items-center gap-2 truncate"><span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-bold text-ink-mute">{i + 1}</span><span className="truncate font-semibold text-ink">{r.product_name}</span></span>
              <span className="flex shrink-0 items-center gap-2">
                <span className="text-[11px] text-ink-mute">{fmtRp(r.transfer_price)}</span>
                <span className={`font-bold ${(r.central_margin_pct ?? 0) < 0 ? "text-danger-text" : positive ? "text-success-text" : "text-ink"}`}>{r.central_margin_pct}%</span>
              </span>
            </div>
          ))}
        </div>
      ) : <div className="flex h-[120px] items-center justify-center text-sm text-ink-mute">{t("No finished goods to analyze yet")}</div>}
    </Card>
  );
}

function Kpi({ icon: Icon, label, value, tone = "primary", testid }) {
  const tones = { primary: "bg-primary-light text-primary", info: "bg-blue-50 text-blue-600", warn: "bg-amber-50 text-amber-600", danger: "bg-danger-light text-danger-text" };
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}><Icon size={18} /></span>
        <div className="min-w-0"><p className="truncate text-xs font-semibold uppercase text-ink-mute">{label}</p><p className="truncate text-lg font-bold text-ink">{value}</p></div>
      </div>
    </Card>
  );
}
